import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { getCheckoutStatus } from "../lib/api";
import BrandBadge from "../components/BrandBadge";

const MAX_ATTEMPTS = 6;

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState("polling"); // polling | success | failed | timeout
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    let timer;

    const poll = async () => {
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("success");
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-4 md:px-8 py-24 min-h-[60vh] text-center" data-testid="checkout-success-page">
      {state === "polling" && (
        <div className="flex flex-col items-center gap-6">
          <Loader2 className="w-12 h-12 text-royal animate-spin" />
          <h1 className="font-heading text-2xl font-semibold text-navy-900">Confirming your payment…</h1>
          <p className="text-slate-500">Hang tight, this only takes a moment.</p>
        </div>
      )}

      {state === "success" && (
        <div className="flex flex-col items-center gap-5">
          <BrandBadge size={110} />
          <CheckCircle2 className="w-14 h-14 text-emerald-500" />
          <h1 className="font-heading text-3xl font-bold text-navy-900" data-testid="success-heading">Thank you for your order!</h1>
          <p className="text-slate-500 max-w-sm">
            Your payment was successful and your daily ritual is on its way. A confirmation has been sent to your email.
          </p>
          {order?.item_summary?.length > 0 && (
            <div className="w-full bg-[#FAFBFD] rounded-2xl border border-slate-100 p-6 text-left mt-2">
              <p className="text-xs font-bold tracking-widest uppercase text-slate-400 mb-3">Order Summary</p>
              <ul className="space-y-2">
                {order.item_summary.map((s, i) => (
                  <li key={i} className="text-sm text-navy-900 flex justify-between">{s}</li>
                ))}
              </ul>
              {order.amount_total != null && (
                <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between font-semibold text-navy-900">
                  <span>Total paid</span>
                  <span>€{(order.amount_total / 100).toFixed(2)}</span>
                </div>
              )}
            </div>
          )}
          <Link to="/" className="mt-4 inline-flex items-center gap-2 bg-navy-900 text-white rounded-full px-7 py-3.5 font-medium hover:bg-navy-800 transition-colors">
            Back to Home <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {(state === "failed" || state === "timeout") && (
        <div className="flex flex-col items-center gap-5">
          <XCircle className="w-14 h-14 text-amber-500" />
          <h1 className="font-heading text-2xl font-semibold text-navy-900">
            {state === "timeout" ? "Still processing…" : "Payment not completed"}
          </h1>
          <p className="text-slate-500 max-w-sm">
            {state === "timeout"
              ? "Your payment is taking longer than expected. Please check your email for confirmation."
              : "We couldn't confirm your payment. If you were charged, please contact support."}
          </p>
          <Link to="/cart" className="inline-flex items-center gap-2 bg-navy-900 text-white rounded-full px-7 py-3.5 font-medium hover:bg-navy-800 transition-colors">
            Return to cart
          </Link>
        </div>
      )}
    </div>
  );
};

export default CheckoutSuccess;
